import React from "react";
import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import BrandMark from "./BrandMark";
import { CONTACT, LICENSES, BRAND } from "../lib/content";

const construction = [
  { to: "/custom-homes", label: "Custom Homes" },
  { to: "/renovations", label: "Renovations & Additions" },
  { to: "/kitchen-and-bath", label: "Kitchen & Bath" },
  { to: "/commercial-construction", label: "Commercial" },
];

const recovery = [
  { to: "/disaster-recovery", label: "Disaster Recovery & FEMA PA" },
  { to: "/federal", label: "Federal Construction & Teaming" },
  { to: "/expert-witness", label: "Expert Witness" },
];

const company = [
  { to: "/about", label: "Our Story" },
  { to: "/team", label: "Leadership & Team" },
  { to: "/projects", label: "Our Work" },
  { to: "/work-with-us", label: "Work With Us" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

function ColumnHeader({ children }) {
  return (
    <div className="text-[11px] tracking-[0.18em] uppercase text-cream-light/60 mb-4 font-semibold">
      {children}
    </div>
  );
}

function ColumnLinks({ items }) {
  return (
    <ul className="space-y-2 text-sm text-cream-light/85">
      {items.map((l) => (
        <li key={l.to}>
          <Link to={l.to} className="hover:text-copper-light transition-colors">{l.label}</Link>
        </li>
      ))}
    </ul>
  );
}

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-ink text-cream-light">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-14">
        <div className="grid grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-4">
            <BrandMark variant="compact" tone="light" size="md" />
            <p className="text-cream-light/75 text-sm leading-relaxed mt-6 max-w-md">
              Family-owned Tampa general contractor since 2006. Custom homes, renovations, commercial and federal construction, disaster recovery and expert witness services.
            </p>
          </div>

          <div className="col-span-6 md:col-span-2">
            <ColumnHeader>Construction</ColumnHeader>
            <ColumnLinks items={construction} />
          </div>

          <div className="col-span-6 md:col-span-3">
            <ColumnHeader>Disaster Recovery</ColumnHeader>
            <ColumnLinks items={recovery} />
          </div>

          <div className="col-span-12 md:col-span-3">
            <ColumnHeader>Company</ColumnHeader>
            <ColumnLinks items={company} />
          </div>
        </div>

        {/* Contact block */}
        <div className="border-t border-cream-light/15 mt-12 pt-8 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-8 text-sm text-cream-light/85 leading-relaxed">
            Serving {CONTACT.location} ·{" "}
            <a href={CONTACT.phoneHref} className="hover:text-copper-light">{CONTACT.phone}</a> ·{" "}
            <a href={`mailto:${CONTACT.email}`} className="hover:text-copper-light break-all">{CONTACT.email}</a> ·{" "}
            {CONTACT.hours}
            <div className="mt-2 text-cream-light/70 text-xs">
              <span className="text-cream-light/55">Mailing address:</span> {CONTACT.mailingAddress}
            </div>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <a
              href={CONTACT.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin"
              className="inline-flex items-center gap-2 text-cream-light/85 hover:text-copper-light text-sm"
            >
              <Linkedin size={18} /> LinkedIn
            </a>
          </div>
        </div>

        {/* License line */}
        <div className="border-t border-cream-light/15 mt-8 pt-6 text-[11px] tracking-[0.08em] text-cream-light/60 leading-relaxed">
          {LICENSES.pairedLabel} · {LICENSES.gc} · {LICENSES.plumb} · {LICENSES.pe}
        </div>

        {/* Bottom line */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-[11px] tracking-[0.1em] uppercase text-cream-light/55">
          <div>
            © {new Date().getFullYear()} {BRAND.legalName} · Service-Disabled Veteran-Owned Small Business
          </div>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-copper-light">Privacy</Link>
            <Link to="/terms" className="hover:text-copper-light">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
